import React, { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { FaTimes, FaClock, FaMicrophone, FaMusic, FaRegCalendar } from 'react-icons/fa';
import { MdLiveTv } from 'react-icons/md';
import { getRadioEPGAction } from '../../../../Redux/Actions/RadioStationAction';
import './Styles/RadioEPGModal.css';

const RadioEPGModal = ({ station, onClose }) => {
    const dispatch = useDispatch();
    const { epg } = useSelector((state) => state.get_radio_Reducers?.stationDetails || {});
    const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0]);

    useEffect(() => {
        if (station?.id) {
            dispatch(getRadioEPGAction(station.id, new Date(selectedDate)));
        }
    }, [station?.id, selectedDate, dispatch]);

    const handleDateChange = (e) => {
        setSelectedDate(e.target.value);
    };

    const getCurrentProgram = () => {
        if (!epg.data?.programs) return null;
        return epg.data.programs.find(program => program.is_current);
    };

    const getTimeSlots = () => {
        if (!epg.data?.programs) return [];
        return epg.data.programs;
    };

    const formatTime = (time) => {
        return time;
    };

    const currentProgram = getCurrentProgram();
    const programs = getTimeSlots();

    return (
        <div className="radio-epg-modal-overlay">
            <div className="radio-epg-modal theme-bg-color">
                {/* Header */}
                <div className="epg-modal-header">
                    <div className="epg-header-info">
                        <div className="station-info">
                            <img 
                                src={station?.image_URL || '/default-radio-icon.png'} 
                                alt={station?.title}
                                className="station-logo"
                                onError={(e) => {
                                    e.target.src = '/default-radio-icon.png';
                                }}
                            />
                            <div className="station-details">
                                <h2 className="station-title theme-text-color">{station?.title}</h2>
                                <div className="live-indicator">
                                    <MdLiveTv className="live-icon" />
                                    <span className="theme-text-color">LIVE</span>
                                </div>
                            </div>
                        </div>
                        <button 
                            className="close-btn theme-text-color"
                            onClick={onClose}
                            aria-label="Close EPG"
                        >
                            <FaTimes />
                        </button>
                    </div>

                    {/* Date Selector */}
                    <div className="date-selector">
                        <FaRegCalendar className="calendar-icon theme-text-color" />
                        <input
                            type="date"
                            value={selectedDate}
                            onChange={handleDateChange}
                            className="date-input theme-bg-color-secondary theme-text-color"
                            min={new Date().toISOString().split('T')[0]}
                            max={new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString().split('T')[0]}
                        />
                    </div>
                </div>

                {/* Current Program Highlight */}
                {currentProgram && (
                    <div className="current-program-highlight theme-bg-color-secondary">
                        <div className="current-program-badge">
                            <MdLiveTv className="live-icon" />
                            <span>NOW PLAYING</span>
                        </div>
                        <div className="current-program-info">
                            <h3 className="program-title theme-text-color">{currentProgram.title}</h3>
                            <p className="program-description theme-text-color opacity-75">
                                {currentProgram.description}
                            </p>
                            <div className="program-time">
                                <FaClock className="clock-icon" />
                                <span className="time-range theme-text-color">
                                    {formatTime(currentProgram.start_time)} - {formatTime(currentProgram.end_time)}
                                </span>
                            </div>
                        </div>
                    </div>
                )}

                {/* Program Schedule */}
                <div className="program-schedule">
                    <h3 className="schedule-title theme-text-color">
                        <FaClock className="schedule-icon" />
                        Program Schedule
                    </h3>

                    {epg.loading ? (
                        <div className="epg-loading">
                            <div className="loading-spinner"></div>
                            <span className="theme-text-color">Loading program guide...</span>
                        </div>
                    ) : epg.error ? (
                        <div className="epg-error theme-text-color">
                            <p>Failed to load program guide</p>
                            <button 
                                className="retry-btn theme-button-bg-color theme-button-text-color"
                                onClick={() => dispatch(getRadioEPGAction(station.id, new Date(selectedDate)))}
                            >
                                Retry
                            </button>
                        </div>
                    ) : programs.length === 0 ? (
                        <div className="no-programs theme-text-color">
                            <FaMicrophone className="no-programs-icon" />
                            <p>No program information available for this date</p>
                        </div>
                    ) : (
                        <div className="programs-list">
                            {programs.map((program) => (
                                <div 
                                    key={program.id}
                                    className={`program-item ${program.is_current ? 'current-program' : ''} theme-bg-color-secondary`}
                                >
                                    <div className="program-time-slot">
                                        <span className="start-time theme-text-color">
                                            {formatTime(program.start_time)}
                                        </span>
                                        <span className="end-time theme-text-color opacity-75">
                                            {formatTime(program.end_time)}
                                        </span>
                                    </div>
                                    
                                    <div className="program-content">
                                        <div className="program-header">
                                            <h4 className="program-name theme-text-color">
                                                {program.title}
                                            </h4>
                                            {program.is_current && (
                                                <div className="live-badge">
                                                    <MdLiveTv className="live-icon" />
                                                    <span>LIVE</span>
                                                </div>
                                            )}
                                        </div>
                                        
                                        <p className="program-desc theme-text-color opacity-75">
                                            {program.description}
                                        </p>
                                        
                                        <div className="program-meta">
                                            <span className="program-type">
                                                <FaMusic className="meta-icon" />
                                                {program.type || 'Music'}
                                            </span>
                                            <span className="program-duration theme-text-color opacity-50">
                                                {program.duration || 'Live'}
                                            </span>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>

                {/* Footer */}
                <div className="epg-modal-footer">
                    <div className="epg-info theme-text-color opacity-75">
                        <FaClock className="info-icon" />
                        <span>All times are in your local timezone</span>
                    </div>
                    <button 
                        className="close-modal-btn theme-button-bg-color theme-button-text-color"
                        onClick={onClose}
                    >
                        Close Guide
                    </button>
                </div>
            </div>
        </div>
    );
};

export default RadioEPGModal;